import { createFileRoute, Link } from "@tanstack/react-router";
import { useLoaderData } from "@tanstack/react-router";
import { MapPin } from "lucide-react";
import { useLocale } from "@/lib/i18n/locale-context";
import { getPageCopy } from "@/lib/i18n/page-copy";

const BASE = "https://sellavo.com";
const TITLE = "Köpa och sälja begagnat på din ort | Sellavo";
const DESCRIPTION =
  "Hitta hur du köper och säljer begagnat där du bor. Guider för 25 svenska orter med tips om upphämtning, frakt och vad som säljer bäst lokalt.";

export const Route = createFileRoute("/kopa-och-salja/")({
  // Ortslistan är stor – hämtas som eget paket i stället för i startpaketet.
  loader: async () => {
    const { CITIES } = await import("@/lib/orter-seo");
    return { cities: CITIES.map((c) => ({ slug: c.slug, name: c.name })) };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${BASE}/kopa-och-salja` },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: `${BASE}/kopa-och-salja` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Köpa och sälja begagnat i Sverige",
          itemListElement: (loaderData?.cities ?? []).map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.name,
            url: `${BASE}/kopa-och-salja/${c.slug}`,
          })),
        }),
      },
    ],
  }),
  component: CityHub,
});

export function CityHub() {
  const { cities } = useLoaderData({ strict: false }) as { cities: { slug: string; name: string }[] };
  const { lang } = useLocale();
  const copy = lang === "sv" ? null : getPageCopy(lang).city;
  return (
    <main className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <p className="font-heading text-[11px] font-bold uppercase tracking-wide text-primary">
        {copy?.eyebrow ?? "Orter"}
      </p>
      <h1 className="mt-2 font-heading text-3xl font-extrabold leading-tight tracking-tight text-foreground sm:text-4xl">
        {copy?.title ?? "Köpa och sälja begagnat där du bor"}
      </h1>
      <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
        {copy?.intro ?? "Det mesta går att skicka, men skrymmande saker säljs oftast lokalt. Här är hur köp och försäljning fungerar på 25 svenska orter – med tips om upphämtning, frakt och vad som brukar gå snabbast att bli av med."}
      </p>

      <ul className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cities.map((city) => (
          <li key={city.slug}>
            <Link
              to="/kopa-och-salja/$ort"
              params={{ ort: city.slug }}
              className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium text-foreground transition-colors hover:border-primary/50"
            >
              <MapPin className="h-4 w-4 shrink-0 text-primary" />
              {city.name}
            </Link>
          </li>
        ))}
      </ul>
    </main>
  );
}
